import { Download } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader } from "@/components/common/loader";
import { listVersions, openDocument } from "@/features/documents/api";
import type { ClientDocument } from "@/features/documents/types";

interface VersionHistoryDialogProps {
  document: ClientDocument | null;
  onOpenChange: (open: boolean) => void;
}

/** Lists every revision of a document (newest first) with a per-version download. */
export function VersionHistoryDialog({ document, onOpenChange }: VersionHistoryDialogProps) {
  const [versions, setVersions] = useState<ClientDocument[] | null>(null);

  useEffect(() => {
    if (!document) {
      setVersions(null);
      return;
    }
    let active = true;
    setVersions(null);
    listVersions(document.id)
      .then((v) => active && setVersions(v))
      .catch(() => active && setVersions([]));
    return () => {
      active = false;
    };
  }, [document]);

  return (
    <Dialog open={document !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Version history</DialogTitle>
        </DialogHeader>
        {versions === null ? (
          <Loader />
        ) : versions.length === 0 ? (
          <p className="text-muted-foreground text-sm">No versions found.</p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {versions.map((v) => (
              <li key={v.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="bg-muted rounded px-2 py-0.5 text-xs font-semibold">
                  v{v.version}
                </span>
                <span className="text-muted-foreground flex-1">
                  {new Date(v.createdAt).toLocaleString()}
                </span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Download version ${v.version}`}
                  onClick={() =>
                    openDocument(v.id).catch(() => toast.error("Couldn't open version."))
                  }
                >
                  <Download className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
